/**
 * Warehouse (Capacities) display formatters — matched 1-1 to the legacy render.
 * Legacy dates use `toLocaleString('en-US', …)` → e.g. "Jul 21, 2026, 02:06 PM"
 * for date-times and `toLocaleDateString('vi-VN')` → "21/07/2026" for plain
 * dates; currency is VND. Kept local to the module so other screens' global
 * `formatDateTime` (day/month) is untouched.
 */

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

/**
 * "2 days ago" — relative time for list "Created" columns, matching the legacy
 * warehouse list (`dayjs().fromNow()`). Pair with an absolute tooltip
 * (`formatWhDateTime`) so the exact time stays one hover away.
 */
export function formatWhRelative(iso?: string | null): string {
  if (!iso) return '—';
  const d = dayjs(iso);
  return d.isValid() ? d.fromNow() : '—';
}

/** "Jul 21, 2026, 02:06 PM" — legacy process/detail date-time. */
export function formatWhDateTime(iso?: string | null): string {
  if (!iso) return 'N/A';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'N/A';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** "21/07/2026" — legacy import-items date columns. */
export function formatWhDate(iso?: string | null): string {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('vi-VN');
}

/** VND currency, legacy `Intl.NumberFormat('vi-VN', {currency:'VND'})`. */
export function formatWhCurrency(amount?: number | null): string {
  if (!amount) return '-';
  return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(amount);
}

/**
 * Preview ref following the backend legacy pattern
 * `CATEGORY.CLEANNAME.YYYYMM` (optionally prefixed with a parent ref) — used for
 * not-yet-created import items that have no service tags.
 */
export function generateRefPreview(
  category: string,
  name: string,
  purchaseDate?: string,
  parentRef?: string,
): string {
  const cat = (category || '').toUpperCase();
  const cleanName = (name || '')
    .toUpperCase()
    .replace(/[^A-Z0-9\s]/g, '')
    .trim()
    .slice(0, 20);
  const date = purchaseDate ? new Date(purchaseDate) : new Date();
  const yyyymm = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}`;
  const ref = cleanName ? `${cat}.${cleanName}.${yyyymm}` : `${cat}.${yyyymm}`;
  return parentRef ? `${parentRef}_${ref}` : ref;
}
